import React, { useEffect, useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { Authenticator } from '@aws-amplify/ui-react';
import { Amplify } from 'aws-amplify';
import { redirectToOktaIfSignedOut } from './lib/oktaAutoRedirect';
import { Toaster } from 'react-hot-toast';
import '@aws-amplify/ui-react/styles.css';
import { DashboardOverview } from './components/dashboard';
import { TaskList } from './components/tasks';
import { PartnerList } from './components/partners';
import { ConnectionList } from './components/connections';
import { FileAuditPage } from './components/fileAudit';
import { Layout, Header, Navigation, NavigationTab } from './components/layout';
import { ThemeProvider, ThemeWrapper } from './contexts/ThemeContext';
// THUB-186: display-timezone preference, mirroring the theme provider.
import { TimezoneProvider } from './contexts/TimezoneContext';
import { WebSocketProvider, useWebSocket } from './contexts/WebSocketContext';
import { RoleProvider } from './contexts/RoleContext';
import { queryClient } from './lib/queryClient';
import { queryKeys } from './lib/queryKeys';
import { INVALIDATING_TOPICS, keysForBroadcast } from './lib/wsInvalidationMap';
import { usePartnerData, useAnalytics } from './hooks';
import { useCoalescedInvalidate } from './hooks/useCoalescedInvalidate';
import { ErrorBoundary, LoadingState } from './components/shared';
import { RoleGuard } from './components/RoleGuard';
import { AdminPanel } from './components/admin';
import { EnhancedPartner } from './types/partner';
import { appConfig, setAppConfig, AppConfig, getEnvLabel } from './config';
import './styles/colors.css';

/**
 * Set the browser tab title from the current env, e.g. "TransferHub [NP]".
 * Falls back to the bare product name when env is absent (local dev).
 */
function applyDocumentTitle(env?: string | null) {
  const label = getEnvLabel(env);
  document.title = label ? `TransferHub [${label}]` : 'TransferHub';
}

// Re-export appConfig for backwards compatibility
export { appConfig };

function App() {
  const [configLoaded, setConfigLoaded] = useState(false);
  const [env, setEnv] = useState<string | undefined>(undefined);

  useEffect(() => {
    // Check if user is coming from Okta
    const urlParams = new URLSearchParams(window.location.search);
    const issuer = urlParams.get('iss');
    const forceLocal = urlParams.get('auth') === 'local'; // Allow bypassing auto-redirect
    // Decode the URL-encoded issuer parameter from Okta
    const decodedIssuer = issuer ? decodeURIComponent(issuer) : null;
    const isFromOkta = decodedIssuer === 'https://cgic.okta.com';

    // Load config from config.json (deployed by CDK)
    fetch('/config.json')
      .then(res => res.json())
      .then((config: AppConfig) => {
        setAppConfig(config);
        setEnv(config.env);
        applyDocumentTitle(config.env);

        Amplify.configure({
          Auth: {
            Cognito: {
              userPoolId: config.userPoolId,
              userPoolClientId: config.userPoolClientId,
              ...(config.cognitoDomain && {
                loginWith: {
                  oauth: {
                    domain: config.cognitoDomain.replace('https://', ''),
                    scopes: ['openid', 'profile', 'email'],
                    redirectSignIn: [window.location.origin],
                    redirectSignOut: [window.location.origin],
                    responseType: 'code'
                  }
                }
              })
            }
          }
        });

        setConfigLoaded(true);

        // Auto-redirect to Okta if enabled and not bypassed
        if (config.oktaAutoRedirect && config.cognitoDomain && !forceLocal && !isFromOkta) {
          // Guarded: an already-signed-in user needs no redirect, and asking Amplify anyway
          // threw UserAlreadyAuthenticatedException on every page load.
          void redirectToOktaIfSignedOut();
          return; // Don't continue with normal flow
        }

        // If user came from Okta, redirect to Cognito OAuth flow
        if (isFromOkta && config.cognitoDomain) {
          console.log('User came from Okta (decoded issuer:', decodedIssuer, '), redirecting to Cognito OAuth...');
          void redirectToOktaIfSignedOut();
        }
      })
      .catch(err => {
        console.error('Failed to load config:', err);
        // Fallback to environment variables for local dev
        const fallbackConfig: AppConfig = {
          apiEndpoint: import.meta.env.VITE_API_ENDPOINT || '',
          userPoolId: import.meta.env.VITE_USER_POOL_ID || '',
          userPoolClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID || '',
          env: import.meta.env.VITE_ENV || 'sbx',
        };
        setAppConfig(fallbackConfig);
        setEnv(fallbackConfig.env);
        applyDocumentTitle(fallbackConfig.env);

        Amplify.configure({
          Auth: {
            Cognito: {
              userPoolId: fallbackConfig.userPoolId,
              userPoolClientId: fallbackConfig.userPoolClientId,
            }
          }
        });

        setConfigLoaded(true);
      });
  }, []);

  if (!configLoaded) {
    return <div className="text-center p-8 text-xl text-neutral-900 dark:text-slate-50">Loading configuration...</div>;
  }

  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <TimezoneProvider>
          <ThemeWrapper>
            <Toaster
              position="top-right"
              reverseOrder={false}
              gutter={8}
              toastOptions={{
                duration: 6000,
                success: {
                  duration: 6000,
                  iconTheme: {
                    primary: '#10b981',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 10000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
            <ErrorBoundary
              onError={(error, errorInfo) => {
                console.error('App Error:', error, errorInfo);
              }}
            >
              <WebSocketProvider
                url={appConfig?.webSocketEndpoint}
                autoConnect={true}
              >
                <Authenticator>
                  {({ signOut, user }) => (
                    <RoleProvider>
                      <AppShell signOut={signOut} user={user} env={env} />
                    </RoleProvider>
                  )}
                </Authenticator>
              </WebSocketProvider>
            </ErrorBoundary>
          </ThemeWrapper>
          </TimezoneProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}

/**
 * AppShell - handles routing and navigation state
 */
interface AppShellProps {
  signOut?: () => void;
  user?: any;
  env?: string;
}

const AppShell: React.FC<AppShellProps> = ({ signOut, user, env }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Derive active tab from current route
  const getActiveTab = (): NavigationTab => {
    const path = location.pathname;
    if (path.startsWith('/partners')) return 'partners';
    if (path.startsWith('/connections')) return 'connections';
    if (path.startsWith('/tasks')) return 'tasks';
    if (path.startsWith('/transfer-history')) return 'transfer-history';
    if (path.startsWith('/admin')) return null as unknown as NavigationTab;
    return 'dashboard';
  };

  const activeTab = getActiveTab();

  // Handle tab change by navigating to the appropriate route
  const handleTabChange = (tab: NavigationTab) => {
    switch (tab) {
      case 'dashboard':
        navigate('/');
        break;
      case 'partners':
        navigate('/partners');
        break;
      case 'connections':
        navigate('/connections');
        break;
      case 'tasks':
        navigate('/tasks');
        break;
      case 'transfer-history':
        navigate('/transfer-history');
        break;
    }
  };

  return (
    <Layout
      header={
        <Header
          user={{
            name: user?.signInDetails?.loginId,
            email: user?.signInDetails?.loginId
          }}
          onSignOut={signOut}
          env={env}
        />
      }
      navigation={
        <Navigation
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      }
    >
      {/* THUB-290: was a static "Content Error" node, which told an operator nothing and left
          no trace — the error identity was discarded and `componentDidCatch` only wrote a
          console line that died with the tab. A Content Error on the NP Dashboard cost a full
          log-diving pass across five (entirely clean) Lambdas and still did not reach root
          cause. `renderFallback` receives the captured record, so the screen now names what
          failed and where, and every occurrence is persisted for later correlation. */}
      <ErrorBoundary
        resetKeys={[location.pathname]}
        renderFallback={(record, reset) => (
          <div className="flex flex-col items-center justify-center min-h-[400px] text-center text-neutral-600 dark:text-slate-400 px-6">
            <h2 className="m-0 mb-4 text-danger-600 dark:text-danger-400">Content Error</h2>
            <p className="m-0 mb-4 text-lg">
              There was an error loading this section. Please try refreshing the page.
            </p>
            <p className="m-0 mb-2 text-sm font-mono">
              {record.name} in <strong>{record.component}</strong>
            </p>
            <p className="m-0 mb-6 text-sm">
              Quote reference <strong className="font-mono">{record.correlationId}</strong> when
              reporting this.
            </p>
            <details className="text-left max-w-[720px] w-full mb-6">
              <summary className="cursor-pointer text-sm font-semibold hover:text-primary-600 dark:hover:text-primary-400">
                Technical details (for support)
              </summary>
              <pre className="mt-2 p-3 bg-neutral-100 dark:bg-slate-800 border border-neutral-200 dark:border-slate-700 rounded font-mono text-xs whitespace-pre-wrap break-words overflow-x-auto">
                {`${record.at}  ${record.path}\n${record.message}\n\n${record.componentStack}`}
              </pre>
            </details>
            <button
              onClick={reset}
              className="bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white border-none py-2.5 px-5 rounded text-sm font-medium cursor-pointer transition-colors duration-200"
            >
              Try Again
            </button>
          </div>
        )}
      >
        <AppRoutes />
      </ErrorBoundary>
    </Layout>
  );
};

/**
 * AppRoutes - defines all application routes
 */
const AppRoutes: React.FC = () => {
  const queryClientInstance = useQueryClient();
  const {
    partners,
    loading: partnersLoading,
    error: partnersError,
    refreshPartners
  } = usePartnerData({ autoSubscribe: true });

  const { connectionStatus, subscribe } = useWebSocket();

  useAnalytics();

  // Full-cache refresh, used only by the error-state Retry button below.
  const refreshData = useCallback(() => {
    queryClientInstance.invalidateQueries({ queryKey: queryKeys.partners.all });
    queryClientInstance.invalidateQueries({ queryKey: queryKeys.connections.all });
    queryClientInstance.invalidateQueries({ queryKey: queryKeys.tasks.all });
    queryClientInstance.invalidateQueries({ queryKey: queryKeys.analytics.system });
  }, [queryClientInstance]);

  const scheduleInvalidate = useCoalescedInvalidate();

  // THUB-182: subscribe to WebSocket topics for real-time updates. When the
  // backend broadcasts a change, ALL connected clients invalidate their
  // TanStack Query cache and re-fetch — not just the person who made the change.
  //
  // Previously every topic ran the same blanket `refreshData()`, invalidating
  // all four query keys on every broadcast. Since `file_ready` is emitted once
  // per file, a 50-file drop meant 200 refetches per browser. Now each topic
  // maps to the minimal key set (lib/wsInvalidationMap.ts) and the results are
  // coalesced onto one trailing-edge flush, so that same drop is 1 refetch of
  // ['tasks'].
  //
  // `role_mapping_changes` / `policy_changes` are intentionally NOT subscribed
  // here (they map to none of the query keys and have dedicated local
  // subscribers in RoleContext / AdminPanel / PolicyManagement), and
  // `analytics_updates` is dead in every deployed env. See wsInvalidationMap.ts.
  useEffect(() => {
    if (connectionStatus !== 'connected') return;

    const unsubs = INVALIDATING_TOPICS.map(topic =>
      subscribe(topic, (data: any) => {
        scheduleInvalidate(keysForBroadcast(topic, data));
      })
    );

    return () => {
      unsubs.forEach(unsub => unsub());
    };
  }, [connectionStatus, subscribe, scheduleInvalidate]);

  const handlePartnerSelect = useCallback((partner: EnhancedPartner) => {
    console.log('Partner selected:', partner);
  }, []);

  const hasErrors = partnersError;

  if (hasErrors && (!partners || partners.length === 0)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center text-neutral-600 dark:text-slate-400">
        <h2 className="m-0 mb-4 text-danger-600 dark:text-danger-400">System Error</h2>
        <p className="m-0 text-lg">Failed to load system data. Please check your connection and try again.</p>
        <div className="mt-2 text-sm text-danger-600 dark:text-danger-400">
          {partnersError && <p>Partners: {partnersError}</p>}
        </div>
        <button onClick={refreshData} className="btn-primary mt-4">
          Retry
        </button>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <LoadingState loading={false} error={null} size="large">
            <DashboardOverview />
          </LoadingState>
        }
      />
      <Route path="/tasks" element={<TaskList />} />
      <Route path="/tasks/:taskId" element={<TaskList />} />
      <Route
        path="/partners"
        element={
          <LoadingState
            loading={partnersLoading && partners.length === 0}
            error={partnersError}
            onRetry={refreshPartners}
            size="large"
          >
            <PartnerList
              showTaskCounts={true}
              showConnectionStatus={true}
              groupBy="status"
              onPartnerSelect={handlePartnerSelect}
            />
          </LoadingState>
        }
      />
      <Route
        path="/partners/:partnerId"
        element={
          <LoadingState
            loading={partnersLoading && partners.length === 0}
            error={partnersError}
            onRetry={refreshPartners}
            size="large"
          >
            <PartnerList
              showTaskCounts={true}
              showConnectionStatus={true}
              groupBy="status"
              onPartnerSelect={handlePartnerSelect}
            />
          </LoadingState>
        }
      />
      <Route path="/connections" element={<ConnectionList />} />
      <Route path="/connections/:connectionId" element={<ConnectionList />} />
      <Route path="/transfer-history" element={<FileAuditPage />} />
      <Route path="/file-audit" element={<Navigate to="/transfer-history" replace />} />
      <Route
        path="/admin/*"
        element={
          <RoleGuard minimumRole="system_admin">
            <AdminPanel />
          </RoleGuard>
        }
      />
      {/* Catch-all redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default App;
